import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import {
  Clock, Warning, CheckCircle, CaretLeft, CaretRight,
  Flag, FlagPennant, ListChecks, ArrowsOut, Eye, ShieldWarning,
  PaperPlaneTilt, CircleNotch, Webcam, VideoCamera
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CandidateAssessment() {
  const { token } = useParams();

  const [assessment, setAssessment] = useState(null);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [questionVisits, setQuestionVisits] = useState({});
  const [questionTimeSpent, setQuestionTimeSpent] = useState({});
  const [tabSwitches, setTabSwitches] = useState(0);
  const [fullscreenExits, setFullscreenExits] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const [webcamStream, setWebcamStream] = useState(null);
  const [webcamError, setWebcamError] = useState(false);
  const webcamVideoRef = useRef(null);
  const webcamIntervalRef = useRef(null);

  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showFullscreenWarning, setShowFullscreenWarning] = useState(false);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);

  const timerRef = useRef(null);
  const saveRef = useRef(null);
  const submittingRef = useRef(false);
  const timeWarningShown = useRef(false);
  const tabSwitchRef = useRef(0);
  const fsExitRef = useRef(0);
  const qVisitRef = useRef({});
  const answersRef = useRef({});
  const qTimeRef = useRef({});
  const lastQChangeTime = useRef(Date.now());
  const lastQId = useRef(null);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/take/${token}`);
        setAssessment(res.data);
        if (res.data.saved_answers?.length > 0) {
          const restored = {};
          res.data.saved_answers.forEach(a => { restored[a.question_id] = a.selected_options; });
          setAnswers(restored);
        }
        if (res.data.proctoring_data) {
          const pd = res.data.proctoring_data;
          setTabSwitches(pd.tab_switches || 0);
          tabSwitchRef.current = pd.tab_switches || 0;
          setFullscreenExits(pd.fullscreen_exits || 0);
          fsExitRef.current = pd.fullscreen_exits || 0;
          if (pd.question_visits) {
            setQuestionVisits(pd.question_visits);
            qVisitRef.current = pd.question_visits;
          }
          if (pd.question_time_spent) {
            setQuestionTimeSpent(pd.question_time_spent);
            qTimeRef.current = pd.question_time_spent;
          }
        }
        if (res.data.status === "started" && res.data.started_at) {
          setStarted(true);
          const elapsed = Math.floor((Date.now() - new Date(res.data.started_at).getTime()) / 1000);
          const remaining = Math.max(0, res.data.time_limit * 60 - elapsed);
          setTimeLeft(remaining);
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load assessment");
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!started || submitted || timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          doSubmit();
          return 0;
        }
        if (prev === 300 && !timeWarningShown.current) {
          timeWarningShown.current = true;
          setShowTimeWarning(true);
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [started, submitted]);

  useEffect(() => {
    if (!started || submitted) return;
    saveRef.current = setInterval(() => { autoSave(); }, 10000);
    return () => clearInterval(saveRef.current);
  }, [started, submitted]);

  useEffect(() => {
    if (!started || submitted) return;
    const questions = assessment?.questions || [];
    const qId = questions[currentQ]?.question_id;
    if (!qId) return;

    const now = Date.now();
    if (lastQId.current && lastQId.current !== qId) {
      const elapsed = (now - lastQChangeTime.current) / 1000;
      qTimeRef.current = {
        ...qTimeRef.current,
        [lastQId.current]: (qTimeRef.current[lastQId.current] || 0) + elapsed
      };
      setQuestionTimeSpent({ ...qTimeRef.current });
    }
    lastQChangeTime.current = now;
    lastQId.current = qId;

    setQuestionVisits(prev => {
      const updated = { ...prev, [qId]: (prev[qId] || 0) + 1 };
      qVisitRef.current = updated;
      return updated;
    });
  }, [currentQ, started]);

  useEffect(() => {
    if (!started || submitted) return;
    const handler = () => {
      if (document.hidden) {
        tabSwitchRef.current += 1;
        setTabSwitches(tabSwitchRef.current);
        if (tabSwitchRef.current >= 5) {
          doSubmit();
        } else {
          setShowTabWarning(true);
        }
      }
    };
    document.addEventListener("visibilitychange", handler);

    const blurHandler = () => {
      if (started && !submitted) {
        tabSwitchRef.current += 1;
        setTabSwitches(tabSwitchRef.current);
        if (tabSwitchRef.current >= 5) {
          doSubmit();
        }
      }
    };
    window.addEventListener("blur", blurHandler);

    return () => {
      document.removeEventListener("visibilitychange", handler);
      window.removeEventListener("blur", blurHandler);
    };
  }, [started, submitted]);

  useEffect(() => {
    const fsHandler = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (!isFull && started && !submitted) {
        fsExitRef.current += 1;
        setFullscreenExits(fsExitRef.current);
        setShowFullscreenWarning(true);
      }
    };
    document.addEventListener("fullscreenchange", fsHandler);
    return () => document.removeEventListener("fullscreenchange", fsHandler);
  }, [started, submitted]);

  useEffect(() => {
    if (!started) return;
    const prevent = (e) => e.preventDefault();
    document.addEventListener("copy", prevent);
    document.addEventListener("paste", prevent);
    document.addEventListener("contextmenu", prevent);
    return () => {
      document.removeEventListener("copy", prevent);
      document.removeEventListener("paste", prevent);
      document.removeEventListener("contextmenu", prevent);
    };
  }, [started]);

  const enterFullscreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } catch { /* browser may block */ }
  };

  const getProctoringData = () => {
    const now = Date.now();
    let timeData = { ...qTimeRef.current };
    if (lastQId.current) {
      const elapsed = (now - lastQChangeTime.current) / 1000;
      timeData[lastQId.current] = (timeData[lastQId.current] || 0) + elapsed;
      lastQChangeTime.current = now;
    }
    return {
      tab_switches: tabSwitchRef.current,
      fullscreen_exits: fsExitRef.current,
      question_visits: qVisitRef.current,
      question_time_spent: timeData,
      webcam_snapshots: 0,
      webcam_enabled: !!webcamStream
    };
  };

  const autoSave = async () => {
    const answerList = Object.entries(answersRef.current).map(([qid, opts]) => ({
      question_id: qid, selected_options: opts
    }));
    try {
      await axios.post(`${API}/take/${token}/save`, {
        answers: answerList,
        proctoring_data: getProctoringData()
      });
    } catch { /* silent */ }
  };

  const handleStart = async () => {
    try {
      await axios.post(`${API}/take/${token}/start`);
      setStarted(true);
      setTimeLeft(assessment.time_limit * 60);
      if (assessment.require_fullscreen) {
        setShowFullscreenPrompt(true);
      }
      if (assessment.enable_webcam) {
        startWebcam();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start");
    }
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: "user" }
      });
      setWebcamStream(stream);
      if (webcamVideoRef.current) {
        webcamVideoRef.current.srcObject = stream;
      }
      webcamIntervalRef.current = setInterval(() => captureSnapshot(stream), 30000);
    } catch {
      setWebcamError(true);
    }
  };

  const captureSnapshot = async (stream) => {
    try {
      const video = webcamVideoRef.current;
      if (!video || !stream?.active) return;
      const canvas = document.createElement("canvas");
      canvas.width = 320;
      canvas.height = 240;
      canvas.getContext("2d").drawImage(video, 0, 0, 320, 240);
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const form = new FormData();
        form.append("file", blob, "snapshot.jpg");
        try {
          await axios.post(`${API}/take/${token}/snapshot`, form);
        } catch { /* silent */ }
      }, "image/jpeg", 0.6);
    } catch { /* silent */ }
  };

  const stopWebcam = () => {
    clearInterval(webcamIntervalRef.current);
    if (webcamStream) {
      webcamStream.getTracks().forEach(t => t.stop());
      setWebcamStream(null);
    }
  };

  const doSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    clearInterval(timerRef.current);
    clearInterval(saveRef.current);

    const answerList = Object.entries(answersRef.current).map(([qid, opts]) => ({
      question_id: qid, selected_options: opts
    }));
    try {
      const res = await axios.post(`${API}/take/${token}/submit`, {
        answers: answerList,
        proctoring_data: getProctoringData()
      });
      setResult(res.data);
      setSubmitted(true);
      stopWebcam();
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Submit failed");
      submittingRef.current = false;
    }
  }, [token]);

  const selectOption = (qId, optIdx, type) => {
    if (!assessment?.allow_change_answers) {
      if (answers[qId]?.length > 0) return;
    }
    setAnswers(prev => {
      const current = prev[qId] || [];
      if (type === "single") {
        return { ...prev, [qId]: [optIdx] };
      }
      const next = current.includes(optIdx)
        ? current.filter(i => i !== optIdx)
        : [...current, optIdx];
      return { ...prev, [qId]: next };
    });
  };

  const toggleReview = (qId) => {
    setMarkedForReview(prev => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId); else next.add(qId);
      return next;
    });
  };

  const goToQuestion = (idx) => {
    setCurrentQ(idx);
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="assessment-error">
        <div className="bg-white border border-black/10 p-8 max-w-md text-center">
          <Warning size={48} weight="duotone" className="text-[#EF4444] mx-auto mb-4" />
          <h2 className="font-heading font-bold text-xl text-[#0A0A0A] mb-2">Assessment Unavailable</h2>
          <p className="text-sm text-[#52525B]">{error}</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="assessment-loading">
        <CircleNotch size={32} className="text-[#FF3366] animate-spin" />
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="assessment-submitted">
        <div className="bg-white border border-black/10 p-8 max-w-lg w-full">
          <CheckCircle size={56} weight="duotone" className="text-[#10B981] mx-auto mb-4" />
          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-1 text-center">Assessment Submitted</h2>
          <p className="text-sm text-[#52525B] mb-6 text-center">Your responses have been recorded successfully.</p>
          <div className="grid grid-cols-3 gap-0 border border-black/10">
            <div className="p-4 text-center border-r border-black/10">
              <p className="text-3xl font-heading font-black text-[#0A0A0A] tabular-nums">{result.score}%</p>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] mt-1">Score</p>
            </div>
            <div className="p-4 text-center border-r border-black/10">
              <p className="text-3xl font-heading font-black text-[#10B981] tabular-nums">{result.correct_count}</p>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] mt-1">Correct</p>
            </div>
            <div className="p-4 text-center">
              <p className="text-3xl font-heading font-black text-[#EF4444] tabular-nums">{result.total_questions - result.correct_count}</p>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] mt-1">Incorrect</p>
            </div>
          </div>
          <p className="text-xs text-[#52525B] text-center mt-6">You may close this window.</p>
        </div>
      </div>
    );
  }

  const questions = assessment.questions || [];
  const q = questions[currentQ];
  const answered = Object.keys(answers).filter(k => answers[k]?.length > 0).length;
  const reviewed = markedForReview.size;
  const unanswered = questions.length - answered;

  if (!started) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-6" data-testid="assessment-instructions">
        <div className="bg-white border border-black/10 max-w-xl w-full">
          <div className="px-8 py-5 border-b border-black/10">
            <h1 className="font-heading font-bold text-2xl text-[#0A0A0A]">
              {assessment.assessment_title}
            </h1>
            {assessment.assessment_description && (
              <p className="text-sm text-[#52525B] mt-1">{assessment.assessment_description}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-0 border-b border-black/10">
            {[
              { label: "Questions", value: assessment.total_questions },
              { label: "Duration", value: `${assessment.time_limit} min` },
              { label: "Auto-Save", value: "Every 10s" },
            ].map((item, i) => (
              <div key={i} className={`p-4 text-center ${i < 2 ? 'border-r border-black/10' : ''}`}>
                <p className="font-heading font-black text-xl text-[#0A0A0A]">{item.value}</p>
                <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] mt-1">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="px-8 py-5 space-y-3 border-b border-black/10">
            <h3 className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">Assessment Rules</h3>
            <ul className="space-y-2">
              {[
                { icon: Clock, text: "Timer starts immediately. Assessment auto-submits when time expires." },
                { icon: ShieldWarning, text: "Tab switching is monitored. After 5 switches, your assessment will be auto-submitted." },
                { icon: ArrowsOut, text: assessment.require_fullscreen
                  ? "Fullscreen mode is required. Exiting fullscreen will be recorded."
                  : "Fullscreen mode is recommended for best experience." },
                { icon: ListChecks, text: "Answers are auto-saved every 10 seconds. You can mark questions for review." },
                { icon: Flag, text: assessment.allow_change_answers
                  ? "You can change your answers before submitting."
                  : "Once answered, you cannot change your response." },
                { icon: Eye, text: assessment.allow_review
                  ? "You can review all answers before final submission."
                  : "No review screen before submission." },
                ...(assessment.enable_webcam ? [{
                  icon: VideoCamera, text: "Webcam proctoring is enabled. Your camera will be active during the assessment and periodic snapshots will be captured."
                }] : []),
              ].map((rule, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#52525B]">
                  <rule.icon size={16} className="text-[#0A0A0A] shrink-0 mt-0.5" />
                  <span>{rule.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="px-8 py-5">
            <Button
              onClick={handleStart}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-6 py-3.5 h-auto font-semibold w-full text-sm"
              data-testid="start-assessment-btn"
            >
              <ArrowsOut size={18} className="mr-2" /> Start Assessment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] assessment-focus-mode select-none" data-testid="assessment-in-progress">

      <div className="fixed top-0 left-0 right-0 bg-white border-b border-black/10 z-50" data-testid="assessment-header">
        <div className="flex items-center justify-between px-4 h-12">
          <div className="flex items-center gap-4 min-w-0">
            <span className="text-xs font-semibold text-[#0A0A0A] truncate hidden sm:block max-w-[200px]">
              {assessment.assessment_title}
            </span>
            <div className="flex items-center gap-3 text-[10px] tracking-[0.1em] uppercase font-bold">
              <span className="text-[#10B981]">{answered} answered</span>
              <span className="text-[#52525B]">{unanswered} remaining</span>
              {reviewed > 0 && <span className="text-[#F59E0B]">{reviewed} flagged</span>}
            </div>
          </div>

          <div className={`flex items-center gap-2 px-4 py-1.5 font-heading font-bold tabular-nums text-base
            ${timeLeft <= 60 ? 'bg-[#EF4444] text-white' :
              timeLeft <= 300 ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30' :
              'bg-[#0A0A0A] text-white'}`}
            data-testid="timer-display"
          >
            <Clock size={16} weight="bold" />
            {formatTime(timeLeft)}
          </div>

          <div className="flex items-center gap-2">
            {webcamStream && (
              <span className="text-[9px] px-2 py-0.5 bg-[#10B981]/10 text-[#10B981] font-bold flex items-center gap-1" data-testid="webcam-active">
                <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse" />
                CAM
              </span>
            )}
            {webcamError && assessment?.enable_webcam && (
              <span className="text-[9px] px-2 py-0.5 bg-[#EF4444]/10 text-[#EF4444] font-bold" data-testid="webcam-error">
                CAM OFF
              </span>
            )}
            {tabSwitches > 0 && (
              <span className="text-[9px] px-2 py-0.5 bg-[#EF4444]/10 text-[#EF4444] font-bold" data-testid="tab-switch-count">
                {tabSwitches} tab switch{tabSwitches !== 1 ? 'es' : ''}
              </span>
            )}
            {fullscreenExits > 0 && (
              <span className="text-[9px] px-2 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] font-bold" data-testid="fs-exit-count">
                {fullscreenExits} FS exit{fullscreenExits !== 1 ? 's' : ''}
              </span>
            )}
            <Button
              onClick={() => setShowSubmitConfirm(true)}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-4 py-1.5 h-auto text-xs font-semibold"
              data-testid="header-submit-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1" /> Submit
            </Button>
          </div>
        </div>
        <div className="h-1 bg-black/5">
          <div className="h-full bg-[#FF3366] transition-all duration-300" style={{ width: `${(answered / questions.length) * 100}%` }} />
        </div>
      </div>

      <div className="flex pt-[52px]" style={{ minHeight: 'calc(100vh - 52px)' }}>

        {assessment?.enable_webcam && (
          <video
            ref={webcamVideoRef}
            autoPlay playsInline muted
            className="fixed bottom-4 right-4 w-32 h-24 object-cover border-2 border-[#0A0A0A] z-50 opacity-80"
            data-testid="webcam-preview"
          />
        )}

        <div className={`${showSidebar ? 'w-56' : 'w-0'} shrink-0 bg-white border-r border-black/10 fixed left-0 top-[52px] bottom-0 overflow-y-auto transition-all z-40`}
          data-testid="question-sidebar"
        >
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Questions</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => setShowSidebar(false)} className="text-[#52525B] hover:text-[#0A0A0A] p-0.5 sm:hidden">
                    <CaretLeft size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Hide sidebar</TooltipContent>
              </Tooltip>
            </div>

            <div className="grid grid-cols-2 gap-1 mb-3 text-[9px]">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-[#10B981]" /> <span className="text-[#52525B]">Answered</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 border border-black/20" /> <span className="text-[#52525B]">Not answered</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-[#FF3366]" /> <span className="text-[#52525B]">Current</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 bg-[#F59E0B]" /> <span className="text-[#52525B]">Flagged</span>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-1">
              {questions.map((q, i) => {
                const isAnswered = answers[q.question_id]?.length > 0;
                const isCurrent = i === currentQ;
                const isReview = markedForReview.has(q.question_id);

                let bgClass = "border border-black/15 text-[#52525B] hover:border-black/30";
                if (isCurrent) bgClass = "bg-[#FF3366] text-white border border-[#FF3366]";
                else if (isReview) bgClass = "bg-[#F59E0B] text-white border border-[#F59E0B]";
                else if (isAnswered) bgClass = "bg-[#10B981] text-white border border-[#10B981]";

                return (
                  <button key={i} onClick={() => goToQuestion(i)}
                    className={`w-full aspect-square flex items-center justify-center text-[10px] font-bold transition-all ${bgClass}`}
                    data-testid={`sidebar-q-${i}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-3 border-t border-black/10 space-y-1.5">
              <div className="flex justify-between text-[10px]">
                <span className="text-[#52525B]">Answered</span>
                <span className="font-bold text-[#10B981] tabular-nums">{answered}/{questions.length}</span>
              </div>
              <div className="flex justify-between text-[10px]">
                <span className="text-[#52525B]">Flagged</span>
                <span className="font-bold text-[#F59E0B] tabular-nums">{reviewed}</span>
              </div>
              <div className="flex justify-between text-[10px]">
                <span className="text-[#52525B]">Unanswered</span>
                <span className="font-bold text-[#52525B] tabular-nums">{unanswered}</span>
              </div>
              <Progress value={(answered / questions.length) * 100} className="h-1.5 mt-2 rounded-none [&>div]:bg-[#FF3366]" />
            </div>
          </div>
        </div>

        {!showSidebar && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={() => setShowSidebar(true)}
                className="fixed left-0 top-1/2 -translate-y-1/2 z-40 bg-white border border-black/10 border-l-0 p-2 hover:bg-black/5"
                data-testid="toggle-sidebar-btn"
              >
                <ListChecks size={16} className="text-[#52525B]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Show questions</TooltipContent>
          </Tooltip>
        )}

        <div className={`flex-1 ${showSidebar ? 'ml-56' : 'ml-0'} transition-all`}>
          <div className="max-w-3xl mx-auto px-6 py-8">
            {q && (
              <div key={q.question_id} className="animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-heading font-bold text-[#0A0A0A] tabular-nums">
                      Question {currentQ + 1} of {questions.length}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase
                      ${q.difficulty === 'easy' ? 'bg-[#10B981]/10 text-[#10B981]' :
                        q.difficulty === 'hard' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                        'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                      {q.difficulty}
                    </span>
                    {q.skill_tag && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-white border border-black/10 text-[#52525B]">
                        {q.skill_tag}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => toggleReview(q.question_id)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium transition-colors border
                      ${markedForReview.has(q.question_id)
                        ? 'border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]'
                        : 'border-black/10 text-[#52525B] hover:border-[#F59E0B] hover:text-[#F59E0B]'}`}
                    data-testid="mark-review-btn"
                  >
                    <FlagPennant size={14} weight={markedForReview.has(q.question_id) ? "fill" : "regular"} />
                    {markedForReview.has(q.question_id) ? "Flagged" : "Flag for review"}
                  </button>
                </div>

                <div className="mb-4 text-[10px] text-[#52525B] bg-white border border-black/5 px-3 py-1.5 inline-block">
                  {q.question_type === "multiple" ? "Select all correct answers" : "Select the correct answer"}
                  {!assessment.allow_change_answers && answers[q.question_id]?.length > 0 && (
                    <span className="text-[#EF4444] ml-2 font-semibold">Answer locked</span>
                  )}
                </div>

                <div className="bg-white border border-black/10 p-6 mb-5">
                  <h2 className="font-heading font-semibold text-base sm:text-lg text-[#0A0A0A] leading-relaxed" data-testid="question-text">
                    {q.question_text}
                  </h2>
                </div>

                <div className="space-y-2.5">
                  {q.options.map((opt, oi) => {
                    const selected = (answers[q.question_id] || []).includes(oi);
                    const locked = !assessment.allow_change_answers && answers[q.question_id]?.length > 0;

                    return (
                      <button
                        key={oi}
                        onClick={() => selectOption(q.question_id, oi, q.question_type)}
                        disabled={locked && !selected}
                        className={`w-full text-left px-5 py-4 border transition-all text-sm bg-white group
                          ${selected
                            ? 'border-[#FF3366] ring-1 ring-[#FF3366]/20 text-[#0A0A0A]'
                            : locked
                              ? 'border-black/5 text-[#52525B]/50 cursor-not-allowed'
                              : 'border-black/10 text-[#52525B] hover:border-[#FF3366]/40 hover:text-[#0A0A0A]'}`}
                        data-testid={`option-${currentQ}-${oi}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`w-6 h-6 flex items-center justify-center border text-[11px] font-bold shrink-0 transition-colors
                            ${selected
                              ? 'border-[#FF3366] bg-[#FF3366] text-white'
                              : 'border-black/20 text-[#52525B] group-hover:border-[#FF3366]/40'}
                            ${q.question_type === "multiple" ? '' : 'rounded-full'}`}>
                            {selected ? "\u2713" : String.fromCharCode(65 + oi)}
                          </span>
                          <span className="leading-relaxed">{opt.text}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mt-8 pt-4 border-t border-black/10">
                  <Button
                    variant="outline"
                    onClick={() => goToQuestion(Math.max(0, currentQ - 1))}
                    disabled={currentQ === 0}
                    className="rounded-none border-black/20 text-sm"
                    data-testid="prev-btn"
                  >
                    <CaretLeft size={16} className="mr-1" /> Previous
                  </Button>

                  <span className="text-xs text-[#52525B] tabular-nums">
                    {currentQ + 1} / {questions.length}
                  </span>

                  {currentQ === questions.length - 1 ? (
                    <Button
                      onClick={() => setShowSubmitConfirm(true)}
                      className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-5 py-2.5 h-auto font-semibold text-sm"
                      data-testid="submit-assessment-btn"
                    >
                      Finish & Submit <PaperPlaneTilt size={16} className="ml-1.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => goToQuestion(Math.min(questions.length - 1, currentQ + 1))}
                      className="rounded-none border-black/20 text-sm"
                      data-testid="next-btn"
                    >
                      Next <CaretRight size={16} className="ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showTimeWarning} onOpenChange={setShowTimeWarning}>
        <DialogContent className="rounded-none border-black/10 max-w-sm" data-testid="time-warning-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Clock size={20} className="text-[#F59E0B]" />
              <DialogTitle className="font-heading font-bold text-lg">5 Minutes Remaining</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#52525B]">
              You have 5 minutes left. The assessment will auto-submit when time runs out.
              {unanswered > 0 && ` You still have ${unanswered} unanswered question${unanswered !== 1 ? 's' : ''}.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowTimeWarning(false)}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-4 h-9 text-xs font-semibold"
              data-testid="dismiss-time-warning">
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTabWarning} onOpenChange={setShowTabWarning}>
        <DialogContent className="rounded-none border-black/10 max-w-sm" data-testid="tab-warning-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <ShieldWarning size={20} className="text-[#EF4444]" />
              <DialogTitle className="font-heading font-bold text-lg">Tab Switch Detected</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#52525B]">
              Switching tabs or windows is not allowed during the assessment. This activity has been logged.
              <br /><br />
              <strong className="text-[#EF4444]">Violations: {tabSwitches}/5.</strong> Your assessment will be auto-submitted after 5 violations.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { setShowTabWarning(false); enterFullscreen(); }}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-4 h-9 text-xs font-semibold"
              data-testid="dismiss-tab-warning">
              Return to Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFullscreenWarning} onOpenChange={setShowFullscreenWarning}>
        <DialogContent className="rounded-none border-black/10 max-w-sm" data-testid="fs-warning-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <ArrowsOut size={20} className="text-[#F59E0B]" />
              <DialogTitle className="font-heading font-bold text-lg">Fullscreen Exited</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#52525B]">
              You exited fullscreen mode. This has been recorded. Please return to fullscreen for the best experience.
              <br /><br />
              <strong>Fullscreen exits: {fullscreenExits}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { setShowFullscreenWarning(false); enterFullscreen(); }}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-4 h-9 text-xs font-semibold"
              data-testid="reenter-fullscreen">
              Re-enter Fullscreen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showFullscreenPrompt} onOpenChange={() => {}}>
        <DialogContent className="rounded-none border-black/10 max-w-sm [&>button]:hidden" data-testid="fs-prompt-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <ArrowsOut size={20} className="text-[#0A0A0A]" />
              <DialogTitle className="font-heading font-bold text-lg">Enter Fullscreen</DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#52525B]">
              This assessment requires fullscreen mode. Click below to enter fullscreen and begin.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { enterFullscreen(); setShowFullscreenPrompt(false); }}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-4 h-9 text-xs font-semibold w-full"
              data-testid="enter-fullscreen-btn">
              <ArrowsOut size={16} className="mr-1.5" /> Enter Fullscreen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent className="rounded-none border-black/10 max-w-md" data-testid="submit-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-lg">Submit Assessment?</DialogTitle>
            <DialogDescription className="text-sm text-[#52525B]">
              Please review your progress before submitting.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-0 border border-black/10 my-3">
            <div className="p-3 text-center border-r border-black/10">
              <p className="text-xl font-heading font-black text-[#10B981] tabular-nums">{answered}</p>
              <p className="text-[9px] tracking-wider uppercase font-bold text-[#52525B]">Answered</p>
            </div>
            <div className="p-3 text-center border-r border-black/10">
              <p className="text-xl font-heading font-black text-[#52525B] tabular-nums">{unanswered}</p>
              <p className="text-[9px] tracking-wider uppercase font-bold text-[#52525B]">Unanswered</p>
            </div>
            <div className="p-3 text-center">
              <p className="text-xl font-heading font-black text-[#F59E0B] tabular-nums">{reviewed}</p>
              <p className="text-[9px] tracking-wider uppercase font-bold text-[#52525B]">Flagged</p>
            </div>
          </div>

          {unanswered > 0 && (
            <p className="text-xs text-[#EF4444] font-medium mb-2">
              You have {unanswered} unanswered question{unanswered !== 1 ? 's' : ''}. Unanswered questions will be marked as incorrect.
            </p>
          )}
          {reviewed > 0 && (
            <p className="text-xs text-[#F59E0B] font-medium mb-2">
              You have {reviewed} question{reviewed !== 1 ? 's' : ''} flagged for review.
            </p>
          )}

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowSubmitConfirm(false)}
              className="rounded-none border-black/20 flex-1" data-testid="cancel-submit">
              Review Answers
            </Button>
            <Button onClick={() => { setShowSubmitConfirm(false); doSubmit(); }}
              className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none flex-1 font-semibold"
              data-testid="confirm-submit">
              Submit Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
